// Copyright 2025, University of Maryland, All Rights Reserved

/**
 * Hook that wires useTrackedOperation + runPipelines into a single call.
 * Handles job tracking, status messages, and error state.
 */

import { useCallback, useState } from 'react'
import type { NodeApiClient } from '@/app-shell/services'
import { runPipelines, type RunPipelinesRequest, type RunPipelinesResponse } from '@/subsystems/pipelines'
import { useTrackedOperation } from '@/subsystems/jobs'

export interface UsePipelineRunnerOpts {
  apiClient: NodeApiClient
  orgId: string
  wsId: string
  /** Application key (e.g., 'contract-review', 'srm') */
  appId: string
  /**
   * Called on every job status change during polling. Optional — apps that
   * only need to refetch on completion should leave this unset and use
   * `onAllComplete` instead, to avoid refetching while jobs are still running.
   */
  onRefresh?: () => void
  /** Called when all jobs reach a terminal state. Summary includes any errored job ids. */
  onAllComplete?: (summary: {
    erroredJobIds: string[]
    jobStatuses: Record<string, string>
  }) => void
}

export interface RunOptions {
  agentId: string
  groupId?: string
  targetVersion?: number
  compareVersions?: { old: number; new: number }
}

export interface UsePipelineRunnerResult {
  /** Run pipelines for the given document IDs */
  run: (documentIds: string[], options: RunOptions) => Promise<RunPipelinesResponse>
  /** Whether a run call is currently in flight */
  isRunning: boolean
  /** Job IDs currently being polled */
  activeJobs: string[]
  /** Status message (e.g., "Queued 3 documents...") */
  message: string | null
  /** Error message from last failed run */
  error: string | null
  /** Clear status/error messages */
  clearStatus: () => void
}

export function usePipelineRunner({
  apiClient,
  orgId,
  wsId,
  appId,
  onRefresh,
  onAllComplete,
}: UsePipelineRunnerOpts): UsePipelineRunnerResult {
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { runTracked, isRunning, activeJobs } = useTrackedOperation({
    apiClient,
    orgId,
    workspaceId: wsId,
    onStatusChange: onRefresh,
    onAllComplete: (summary) => {
      setMessage(
        summary.erroredJobIds.length > 0
          ? `${summary.erroredJobIds.length} job(s) failed`
          : 'All jobs completed',
      )
      onAllComplete?.(summary)
    },
  })

  const run = useCallback(
    async (documentIds: string[], options: RunOptions): Promise<RunPipelinesResponse> => {
      setMessage(null)
      setError(null)

      const request: RunPipelinesRequest = {
        documents: documentIds.map(documentId => ({
          documentId,
          ...(options.groupId ? { groupId: options.groupId } : {}),
        })),
        agentId: options.agentId,
        targetVersion: options.targetVersion,
        compareVersions: options.compareVersions,
      }

      try {
        const result = await runTracked(
          () => runPipelines(apiClient, orgId, wsId, appId, request),
          (response) => response.jobs.map(j => j.jobId),
        )

        const count = result.jobs.length
        if (count > 0) {
          setMessage(`Queued ${count} ${count === 1 ? 'document' : 'documents'}. Monitoring progress…`)
        } else {
          setMessage('Pipeline triggered.')
        }
        onRefresh?.()
        return result
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        setError(msg)
        throw err
      }
    },
    [runTracked, apiClient, orgId, wsId, appId, onRefresh],
  )

  const clearStatus = useCallback(() => {
    setMessage(null)
    setError(null)
  }, [])

  return { run, isRunning, activeJobs, message, error, clearStatus }
}
